/* eslint-disable max-len */
import { StoreState } from '@deqo/foundation';
import React, { useEffect, useMemo, useState } from 'react';
import { shuffle } from '../../../CodesForFoundation';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ClientManager } from '../../../concepts/objects';
import { useWindowSizeInRem } from '../../../platform/Hook';
import { useSimpleDrag } from '../../../ui/Hooks';
import { Tip } from '../../../ui/Tip';
import { useResolve } from '../../Container';
import { AccountModel } from '../../account/AccountModel';
import { Avatar } from '../../common/Avatar';
import { Copy, Em, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { BoardInfo } from '../BoardInfo';
import { EditorSession, useEditorSessionObserver } from '../EditorSession';
import { MyRoleInfo } from '../MyRoleInfo';
import { Participant } from '../participants/Participant';
import { ParticipantManager } from '../participants/ParticipantManager';
import { toolEquip } from '../tooling/ToolCommands';
import { ToolManager } from '../tooling/ToolManager';
import { Popover } from '../ui/Popover';
import { Colors } from './../../../ui';
import { Hat } from './Hat';
import { ParticipantActionMenu } from './ParticipantActionMenu';
import { ParticipantListMenu } from './ParticipantListMenu';

const ppl = (count: number) => `${count} ${count === 1 ? 'person' : 'people'}`;

const youTips = shuffle([
    'The Number #1',
    'The Top Dog',
    'The Bees Knees',
    'The Greatest',
    'The Big Cheese',
    'The Hot Stuff',
    'The One And Only',
]);

const ParticipantStripItem: LFC<{ participant: StoreState<Participant>, clickable?: boolean }> = ({ participant: p, clickable }) => {
    const dispatch = useResolve(CommandDispatcher);
    const tools = useResolve(ToolManager);
    const [open, setOpen] = useState(false);
    const tips = p.self ? ['You', ...youTips] : [p.name];

    const dragStart = useSimpleDrag(dist => {
        if (dist.length() > 10) {
            tools.setSettings('tokenTool', {
                value: `(${p.id})`,
                color: p.color,
            });
            dispatch(toolEquip, ['tokenTool', 'drop']);
            return false;
        }
        return true;
    });

    return <Popover
        actionId={`editor.participants.open${p.self ? 'Self' : 'User'}`}
        open={open && clickable}
        onOpenChange={clickable ? setOpen : () => { }}
        placement="left-start"
        inner={{ direction: 'left', offset: '.5' }}
        disabled={!clickable}
        content={<ParticipantActionMenu participant={p} onAction={() => setOpen(false)} />}
    >
        <Flexer relative onMouseDown={(clickable && !open) ? dragStart : undefined}>
            <Tip text={tips} position="left" disabled={open && clickable}>
                <Hat type={p.self ? p.hat : null}>
                    <Avatar.ForUser size="2.5" user={p} clickable={clickable} border="none" />
                </Hat>
            </Tip>
        </Flexer>
    </Popover>;
};

const EveryoneElseStripItem: LFC<{ items: StoreState<Participant>[] }> = ({ items }) => {

    const [open, setOpen] = useState(false);

    return <Popover
        actionId="editor.participants.openEveryoneElse"
        open={open}
        onOpenChange={setOpen}
        placement="left-start"
        inner={{ direction: 'left', offset: '.5' }}
        content={<ParticipantListMenu items={items} onAction={() => setOpen(false)} />}
    >
        <Tip text={open ? null : 'Everyone Else'} position="left">
            <Avatar clickable color={Colors.primary}>
                +{items.length}
            </Avatar>
        </Tip>
    </Popover>;
};

const ObserverInfoView = React.memo(() => {
    const account = useResolve(AccountModel);
    const session = useResolve(EditorSession);
    const { viewers } = session.get(BoardInfo);
    const clients = useResolve(ClientManager);
    const [_ticker, setTicker] = useState(0);

    useEffect(() => clients.on('join', () => setTicker(x => x + 1)), []);
    useEffect(() => clients.on('leave', () => setTicker(x => x + 1)), []);
    useEffect(() => clients.on('update', ([_sender, keys]) => {
        if (keys.includes('track')) {
            setTicker(x => x + 1);
        }
    }), []);

    const untracked = clients.all.filter(x => x.get('track') === false);
    const totalCount = untracked.length;
    const anonCount = untracked.filter(x => x.userId === 'US000000ANON').length;
    const orgCount = totalCount - anonCount;

    return <Flexer width="28" padding="1">
        {!!anonCount && !orgCount && <Title>
            {ppl(anonCount)} viewing anonymously
        </Title>}
        {!anonCount && !!orgCount && <Title>
            {ppl(orgCount)} viewing from {account.label}
        </Title>}
        {!!anonCount && !!orgCount && <Title>
            {ppl(orgCount)} viewing from {account.label} and {ppl(anonCount)} viewing anonymously
        </Title>}
        <Copy flush>
            Currently <Em>
                {viewers === 'world' && 'anyone with the board link'}
                {viewers === 'account' && `anyone from ${account.label}`}
            </Em> can view this board. You can change this via the Share menu in the header.
        </Copy>
    </Flexer>;
});

const useTotalUntrackedClients = () => {

    const clients = useResolve(ClientManager);
    const [ticker, setTicker] = useState(0);

    useEffect(() => clients.on('join', () => setTicker(x => x + 1)), []);
    useEffect(() => clients.on('leave', () => setTicker(x => x + 1)), []);
    useEffect(() => clients.on('update', ([_sender, keys]) => {
        if (keys.includes('track')) {
            setTicker(x => x + 1);
        }
    }), []);

    return useMemo(() => {
        const untracked = clients.all.filter(x => x.get('track') === false);
        return untracked.length;
    }, [ticker]);
};

const ObserverStripItem = React.memo(() => {

    const { canManage } = useEditorSessionObserver(MyRoleInfo);
    const [open, setOpen] = useState(false);

    const totalCount = useTotalUntrackedClients();
    if (!totalCount) return null;

    return <Popover
        open={open && canManage}
        onOpenChange={setOpen}
        placement="left-start"
        inner={{ direction: 'left', offset: '.5' }}
        disabled={!canManage}
        content={<ObserverInfoView />}
    >
        <Tip disabled={open && canManage} text={`${ppl(totalCount)} viewing`} position="left">
            <Avatar clickable={canManage} photo="deqo://icons/i8/incognito" size="2.5" color={Colors.gray300} />
        </Tip>
    </Popover>;
});

const divide = (ptpts: StoreState<Participant>[], limit: number) => {
    const sorter = Participant.createSorter(true);
    const visible = ptpts
        .filter(x => (x.presence === 'active' || x.presence === 'away'))
        .sort(sorter)
        .slice(0, limit);
    const lookup = new Set(visible);
    const cropped = ptpts
        .filter(x => !lookup.has(x))
        .sort(sorter);
    return [visible, cropped] as const;
};

export const useParticipantStripObserver = () => {
    const ptpts = useResolve(ParticipantManager);
    // This basically watches all the Participant instances for changes as well:
    const [all, setAll] = useState<StoreState<Participant>[]>(
        ptpts.filter(x => x.joined).map(x => x.getState())
    );
    useEffect(() => ptpts.on('*', () => setAll(
        ptpts.filter(x => x.joined).map(x => x.getState()))
    ), []);

    // Watch the window height:
    const { height } = useWindowSizeInRem();
    const maxHeight = (height - 8) * .8;
    // Compute maxItems and then sort and divide the list into visible / cropped
    const maxItems = Math.floor(maxHeight / 3);
    return useMemo(() => [all, ...divide(all, maxItems)], [all, maxItems]);
};

export const ParticipantStripWidget = React.memo(() => {

    const { canParticipate } = useEditorSessionObserver(MyRoleInfo);
    const [_all, visible, cropped] = useParticipantStripObserver();

    return <Flexer flex="column" gap=".375">
        {visible.map(x => <ParticipantStripItem key={x.id} participant={x} clickable={canParticipate} />)}
        <ObserverStripItem />
        {!!cropped.length && canParticipate && <EveryoneElseStripItem items={cropped} />}
    </Flexer>;
});
